import Logo from "@/components/Logo";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const STAFF_ROLES = ["super_admin", "admin", "content_editor", "coach"];

const links = [
  { label: "Home", id: "hero" },
  { label: "About", id: "about" },
  { label: "Ecosystem", id: "ecosystem" },
  { label: "Academy", id: "academy" },
  { label: "Pricing", id: "pricing" },
  { label: "Community", id: "community" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const dashHref = user && STAFF_ROLES.includes(user.role) ? "/admin" : "/learn";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex flex-col items-start leading-none"
          data-testid="nav-logo"
        >
          <Logo className="h-6 w-auto" />
          <span className="section-label text-[7px] text-zinc-500 tracking-[0.28em] mt-1">Mind Before Markets</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="section-label text-[11px] text-zinc-400 hover:text-white transition-colors duration-300"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          {user ? (
            <a
              href={dashHref}
              className="section-label text-[11px] text-zinc-400 hover:text-white transition-colors duration-300"
              data-testid="nav-dashboard"
            >
              Dashboard
            </a>
          ) : (
            <a
              href="/login"
              className="section-label text-[11px] text-zinc-400 hover:text-white transition-colors duration-300"
              data-testid="nav-login"
            >
              Login
            </a>
          )}
          <button
            onClick={() => scrollTo("apply")}
            className="btn-solid px-6 py-2.5 text-xs font-medium tracking-wide"
            data-testid="nav-apply-btn"
          >
            Apply
          </button>
        </div>

        <button
          className="md:hidden text-white section-label text-[11px]"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-menu-toggle"
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      {open && (
        <div className="md:hidden backdrop-blur-xl bg-black/90 border-t border-white/10 px-6 py-8 flex flex-col gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                scrollTo(l.id);
                setOpen(false);
              }}
              className="section-label text-sm text-zinc-300 text-left"
              data-testid={`nav-mobile-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <a
              href={dashHref}
              className="section-label text-sm text-zinc-300 text-left"
              data-testid="nav-mobile-dashboard"
            >
              Dashboard
            </a>
          ) : (
            <a
              href="/login"
              className="section-label text-sm text-zinc-300 text-left"
              data-testid="nav-mobile-login"
            >
              Login
            </a>
          )}
          <button
            onClick={() => {
              scrollTo("apply");
              setOpen(false);
            }}
            className="bg-[#FAFAFA] text-[#0A0A0A] px-6 py-3 text-xs font-medium tracking-wide mt-2"
            data-testid="nav-mobile-apply-btn"
          >
            Apply for Mentorship
          </button>
        </div>
      )}
    </motion.header>
  );
}

export { scrollTo };
